import * as React from "react";
import { AlertTriangle, Inbox } from "lucide-react";
import { SkeletonTable } from "./Skeleton";
import { Button } from "./Button";

// StateView — one renderer for loading / error / empty. The error case shows the SERVER'S
// message rather than a generic apology: "name a tenant with ?tenant=<id>" tells an operator
// what to do; "Something went wrong" does not.

export interface StateViewProps {
  loading?: boolean;
  error?: unknown;
  empty?: boolean;
  emptyMessage?: string;
  emptyHint?: string;
  emptyAction?: { label: string; onClick(): void };
  errorContext?: string;
  onRetry?(): void;
  loadingMessage?: string;
  skeleton?: React.ReactNode;
  /** A one-line empty state, for a list inside a section. The full one (icon, 56px of padding each
   *  side) took 200-300px per empty list on a phone — Mail, Mailbox and Recent tasks (2026-11-02). */
  emptyCompact?: boolean;
  children?: React.ReactNode;
}

function messageOf(error: unknown): string | null {
  if (!error) return null;
  if (error instanceof Error && error.message) return error.message;
  if (typeof error === "string") return error;
  return null;
}

export function StateView({
  loading,
  error,
  empty,
  emptyMessage = "Nothing here yet.",
  emptyHint,
  emptyAction,
  errorContext,
  onRetry,
  loadingMessage,
  skeleton,
  emptyCompact,
  children,
}: StateViewProps) {
  if (loading) {
    if (loadingMessage) return <p className="py-3 text-[13px] text-muted">{loadingMessage}</p>;
    return <>{skeleton ?? <SkeletonTable />}</>;
  }

  if (error) {
    const detail = messageOf(error);
    return (
      <div className="flex items-start gap-3 rounded-card border border-[color-mix(in_oklab,var(--ozp-crit)_35%,transparent)] bg-critsoft p-4">
        <AlertTriangle size={17} className="mt-0.5 shrink-0 text-crit" />
        <div className="min-w-0 flex-1">
          <p className="text-[13.5px] font-semibold text-crit">
            {errorContext ? `Could not load ${errorContext}.` : "Could not load this."}
          </p>
          {detail && <p className="mt-1 font-mono text-[12px] break-words text-muted">{detail}</p>}
        </div>
        {onRetry && (
          <Button size="sm" variant="ghost" onClick={onRetry}>
            Retry
          </Button>
        )}
      </div>
    );
  }

  if (empty && emptyCompact) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-card border border-dashed border-line px-3 py-2.5 text-[13px]">
        <span className="text-muted">
          {emptyMessage}
          {emptyHint && <span className="text-faint"> {emptyHint}</span>}
        </span>
        {emptyAction && (
          <Button size="sm" variant="ghost" onClick={emptyAction.onClick}>
            {emptyAction.label}
          </Button>
        )}
      </div>
    );
  }

  if (empty) {
    return (
      <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
        <div className="mb-3 grid size-11 place-items-center rounded-full border border-line bg-surface2 text-faint">
          <Inbox size={18} />
        </div>
        <p className="text-[14px] font-medium text-fg">{emptyMessage}</p>
        {emptyHint && <p className="mt-1 max-w-sm text-[12.5px] text-muted">{emptyHint}</p>}
        {emptyAction && (
          <Button variant="soft" size="sm" className="mt-4" onClick={emptyAction.onClick}>
            {emptyAction.label}
          </Button>
        )}
      </div>
    );
  }

  return <>{children}</>;
}
